/* eslint-disable */
import type { LayerSpecification } from 'maplibre-gl';
import type { Theme } from '../definitions/theme';

type Filter = any[];

const geom = (type: 'Polygon' | 'LineString' | 'Point'): any => ['==', ['geometry-type'], type];
const kind = (value: string): Filter => ['==', ['get', 'kind'], value];
const kindDetail = (value: string): Filter => ['==', ['get', 'kind_detail'], value];
const all = (...parts: Filter[]): any => ['all', ...parts];
const any = (...parts: Filter[]): Filter => ['any', ...parts];
const not = (part: Filter): Filter => ['!', part];

function roadWidth(stops: [number, number, number, number, number, number]) {
    const [z0, v0, z1, v1, z2, v2] = stops;
    return ['interpolate', ['exponential', 1.6], ['zoom'], z0, v0, z1, v1, z2, v2] as any;
}

function roadGap(stops: [number, number, number, number, number, number]) {
    const [z0, v0, z1, v1, z2, v2] = stops;
    return ['interpolate', ['exponential', 1.6], ['zoom'], z0, v0, z1, v1, z2, v2] as any;
}

function roadCasingLayer(
    id: string,
    source: string,
    roadKind: 'other' | 'minor_road' | 'major_road' | 'highway',
    color: string,
    gap: [number, number, number, number, number, number],
    lineWidth?: [number, number, number, number, number, number],
    visibility: 'visible' | 'none' = 'visible',
    extraFilter: Filter[] = [],
    dasharray?: number[],
): LayerSpecification {
    return {
        id,
        type: 'line',
        source,
        'source-layer': 'roads',
        filter: all(kind(roadKind), ...extraFilter),
        paint: {
            'line-color': color,
            ...(dasharray ? { 'line-dasharray': dasharray } : {}),
            'line-gap-width': roadGap(gap),
            ...(lineWidth ? { 'line-width': roadWidth(lineWidth) } : {}),
        },
        layout: {
            visibility,
        },
    };
}

function roadFillLayer(
    id: string,
    source: string,
    roadKind: 'other' | 'minor_road' | 'major_road' | 'highway',
    color: string,
    width: [number, number, number, number, number, number],
    extraFilter: Filter[] = [],
    dasharray?: number[],
): LayerSpecification {
    return {
        id,
        type: 'line',
        source,
        'source-layer': 'roads',
        filter: all(kind(roadKind), ...extraFilter),
        paint: {
            'line-color': color,
            ...(dasharray ? { 'line-dasharray': dasharray } : {}),
            'line-width': roadWidth(width),
        },
    };
}

/**
 * Generate map layers without labels, migrated to current Protomaps basemap schema.
 *
 * Notes:
 * - `pmap:*` properties were removed.
 * - `medium_road` no longer exists; it is folded into `major_road`.
 * - `natural`, `physical_line`, and practical `transit` styling moved elsewhere.
 */
export function generate_layers_without_labels(
    source: string,
    theme: Theme,
): Array<LayerSpecification> {
    const casingVisibility = theme.hasCasings ? 'visible' : 'none';

    const isTunnel = ['==', ['get', 'is_tunnel'], true] as Filter;
    const isBridge = ['==', ['get', 'is_bridge'], true] as Filter;
    const atGrade = all(not(isTunnel), not(isBridge));

    return [
        {
            id: 'background',
            type: 'background',
            paint: {
                'background-color': theme.background,
            },
        },
        {
            'id': 'earth',
            'type': 'fill',
            source,
            'source-layer': 'earth',
            'filter': geom('Polygon'),
            'paint': {
                'fill-color': theme.earth,
            },
        },
        {
            'id': 'landuse_park',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(
                geom('Polygon'),
                any(
                    kind('park'),
                    kind('cemetery'),
                ),
            ),
            'paint': {
                'fill-color': theme.park,
            },
        },
        // add missing green areas
        {
            'id': 'landuse_green',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': [
                'all',
                ['==', ['geometry-type'], 'Polygon'],
                [
                    'in',
                    ['get', 'kind'],
                    [
                        'literal', [
                        'park', // äußerer Stadtgraben
                        'garden', // botanischer Garten
                        'wood', // rechts der Wertach
                        'forest', // Siebentischwald
                        'grass', // neu
                        'meadow', // neu
                        'scrub',
                        'nature_reserve', // Klausenkopf
                        'national_park', // irrelevant
                        'protected_area', // irrelevant
                        'cemetery',
                        'golf_course',
                        'allotments', // neu
                    ],
                    ],
                ],
            ],
            'paint': {
                'fill-color': theme.park,
                // 'fill-color': 'pink',
            },
        },
        {
            'id': 'landuse_hospital',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(geom('Polygon'), kind('hospital')),
            'paint': {
                'fill-color': theme.hospital,
            },
        },
        {
            'id': 'landuse_industrial',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(geom('Polygon'), kind('industrial')),
            'paint': {
                'fill-color': theme.industrial,
            },
        },

        {
            'id': 'landuse_school',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(geom('Polygon'), kind('school')),
            'paint': {
                'fill-color': theme.school,
            },
        },

        // Former natural_wood: natural layer was removed; use landuse kinds.
        {
            'id': 'landuse_wood',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(
                geom('Polygon'),
                any(
                    kind('wood'),
                    kind('forest'),
                    kind('nature_reserve'),
                    kind('national_park'),
                    kind('protected_area'),
                ),
            ),
            'paint': {
                'fill-color': theme.wood,
            },
        },

        // Former highway=footway landuse polygon. Current schema exposes pedestrian/footway as landuse kinds.
        {
            'id': 'landuse_pedestrian',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(
                geom('Polygon'),
                any(
                    kind('pedestrian'),
                    kind('footway'),
                ),
            ),
            'paint': {
                'fill-color': theme.pedestrian,
            },
        },

        {
            'id': 'landuse_scrub',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(
                geom('Polygon'),
                any(
                    kind('scrub'),
                    kind('grass'),
                    kind('meadow'),
                ),
            ),
            'paint': {
                'fill-color': theme.scrub,
            },
        },

        {
            'id': 'landuse_glacier',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(geom('Polygon'), kind('glacier')),
            'paint': {
                'fill-color': theme.glacier,
            },
        },
        {
            'id': 'landuse_sand',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(
                geom('Polygon'),
                any(kind('sand'), kind('beach'), kind('bare_rock')),
            ),
            'paint': {
                'fill-color': theme.sand,
            },
        },

        {
            'id': 'landuse_aerodrome',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(geom('Polygon'), kind('aerodrome')),
            'paint': {
                'fill-color': theme.aerodrome,
            },
        },

        // Former transit_runway: transit moved into roads, and aeroway is a roads kind now.
        {
            'id': 'roads_runway',
            'type': 'line',
            source,
            'source-layer': 'roads',
            'filter': all(
                geom('LineString'),
                kind('aeroway'),
                any(kindDetail('runway'), kindDetail('taxiway')),
            ),
            'paint': {
                'line-color': theme.runway,
                'line-width': 6,
            },
        },

        {
            'id': 'landuse_runway',
            'type': 'fill',
            source,
            'source-layer': 'landuse',
            'filter': all(
                geom('Polygon'),
                any(kind('runway'), kind('taxiway')),
            ),
            'paint': {
                'fill-color': theme.runway,
            },
        },

        // Water is mixed geometry now, so split polygon fill and line waterways.
        {
            'id': 'water',
            'type': 'fill',
            source,
            'source-layer': 'water',
            'filter': geom('Polygon'),
            'paint': {
                'fill-color': theme.water,
            },
        },

        {
            'id': 'waterway',
            'type': 'line',
            source,
            'source-layer': 'water',
            'filter': all(
                geom('LineString'),
                any(
                    kindDetail('canal'),
                    kindDetail('ditch'),
                    kindDetail('drain'),
                    kindDetail('river'),
                    kindDetail('stream'),
                ),
            ),
            'paint': {
                'line-color': theme.water,
                'line-width': 0.5,
            },
        },

        {
            'id': 'buildings',
            'type': 'fill-extrusion',
            source,
            'source-layer': 'buildings',
            'filter': all(geom('Polygon'), any(kind('building'), kind('building_part'))),
            'paint': {
                'fill-extrusion-color': theme.buildings,
                'fill-extrusion-height': 0,
                'fill-extrusion-opacity': 0.5,
            },
        },

        // Tunnels
        roadCasingLayer(
            'roads_tunnels_other_casing',
            source,
            'other',
            theme.tunnel_other_casing,
            [14, 0, 14.5, 0.5, 20, 12],
            undefined,
            casingVisibility,
            [isTunnel],
        ),
        roadFillLayer(
            'roads_tunnels_other',
            source,
            'other',
            theme.tunnel_other,
            [14, 0, 14.5, 0.5, 20, 12],
            [isTunnel],
            [1, 1],
        ),

        roadCasingLayer(
            'roads_tunnels_minor_casing',
            source,
            'minor_road',
            theme.tunnel_minor_casing,
            [12, 0, 12.5, 0.5, 20, 32],
            [12, 0, 12.5, 1, 20, 1],
            casingVisibility,
            [isTunnel],
            [3, 2],
        ),
        roadFillLayer(
            'roads_tunnels_minor',
            source,
            'minor_road',
            theme.tunnel_minor,
            [12, 0, 12.5, 0.5, 20, 32],
            [isTunnel],
        ),

        // medium_road collapsed into major_road in current schema
        roadCasingLayer(
            'roads_tunnels_medium_casing',
            source,
            'major_road',
            theme.tunnel_medium_casing,
            [7, 0, 7.5, 0.5, 20, 32],
            [10, 0, 10.5, 1, 20, 1],
            casingVisibility,
            [isTunnel],
            [3, 2],
        ),
        roadFillLayer(
            'roads_tunnels_medium',
            source,
            'major_road',
            theme.tunnel_medium,
            [7, 0, 7.5, 0.5, 20, 32],
            [isTunnel],
        ),

        roadCasingLayer(
            'roads_tunnels_major_casing',
            source,
            'major_road',
            theme.tunnel_major_casing,
            [7, 0, 7.5, 0.5, 19, 32],
            [9, 0, 9.5, 1, 19, 1],
            casingVisibility,
            [isTunnel],
            [3, 2],
        ),
        roadFillLayer(
            'roads_tunnels_major',
            source,
            'major_road',
            theme.tunnel_major,
            [7, 0, 7.5, 0.5, 19, 32],
            [isTunnel],
        ),

        roadCasingLayer(
            'roads_tunnels_highway_casing',
            source,
            'highway',
            theme.tunnel_highway_casing,
            [3, 0, 3.5, 0.5, 18, 32],
            [7, 0, 7.5, 1, 18, 1],
            casingVisibility,
            [isTunnel],
            [3, 2],
        ),
        roadFillLayer(
            'roads_tunnels_highway',
            source,
            'highway',
            theme.tunnel_highway,
            [3, 0, 3.5, 0.5, 18, 32],
            [isTunnel],
        ),

        // At grade
        roadFillLayer(
            'roads_other',
            source,
            'other',
            theme.other,
            [14, 0, 14.5, 0.5, 20, 12],
            [atGrade],
            [2, 1],
        ),

        roadCasingLayer(
            'roads_minor_casing',
            source,
            'minor_road',
            theme.minor_casing,
            [12, 0, 12.5, 0.5, 20, 32],
            [12, 0, 12.5, 1, 20, 1],
            casingVisibility,
            [atGrade],
        ),
        roadFillLayer(
            'roads_minor',
            source,
            'minor_road',
            theme.minor,
            [12, 0, 12.5, 0.5, 20, 32],
            [atGrade],
        ),

        roadCasingLayer(
            'roads_medium_casing',
            source,
            'major_road',
            theme.medium_casing,
            [7, 0, 7.5, 0.5, 20, 32],
            [10, 0, 10.5, 1, 20, 1],
            casingVisibility,
            [atGrade],
        ),
        roadFillLayer(
            'roads_medium',
            source,
            'major_road',
            theme.medium,
            [7, 0, 7.5, 0.5, 20, 32],
            [atGrade],
        ),

        roadCasingLayer(
            'roads_major_casing',
            source,
            'major_road',
            theme.major_casing,
            [7, 0, 7.5, 0.5, 19, 32],
            [9, 0, 9.5, 1, 19, 1],
            casingVisibility,
            [atGrade],
        ),
        roadFillLayer(
            'roads_major',
            source,
            'major_road',
            theme.major,
            [7, 0, 7.5, 0.5, 19, 32],
            [atGrade],
        ),

        roadCasingLayer(
            'roads_highway_casing',
            source,
            'highway',
            theme.highway_casing,
            [3, 0, 3.5, 0.5, 18, 32],
            [7, 0, 7.5, 1, 18, 1],
            casingVisibility,
            [atGrade],
        ),
        roadFillLayer(
            'roads_highway',
            source,
            'highway',
            theme.highway,
            [3, 0, 3.5, 0.5, 18, 32],
            [atGrade],
        ),

        // Former transit_railway: now roads kind=rail
        {
            'id': 'roads_railway',
            'type': 'line',
            source,
            'source-layer': 'roads',
            'filter': all(geom('LineString'), kind('rail')),
            'paint': {
                'line-color': theme.railway,
                'line-width': 1,
            },
        },
        {
            'id': 'boundaries',
            'type': 'line',
            source,
            'source-layer': 'boundaries',
            'filter': geom('LineString'),
            'paint': {
                'line-color': theme.boundaries,
                'line-width': 0.5,
                'line-dasharray': [3, 2],
            },
        },

        // Bridges
        roadCasingLayer(
            'roads_bridges_other_casing',
            source,
            'other',
            theme.bridges_other_casing,
            [14, 0, 14.5, 0.5, 20, 12],
            undefined,
            casingVisibility,
            [isBridge],
        ),
        roadFillLayer(
            'roads_bridges_other',
            source,
            'other',
            theme.bridges_other,
            [14, 0, 14.5, 0.5, 20, 12],
            [isBridge],
            [2, 1],
        ),

        roadCasingLayer(
            'roads_bridges_minor_casing',
            source,
            'minor_road',
            theme.bridges_minor_casing,
            [12, 0, 12.5, 0.5, 20, 32],
            [12, 0, 12.5, 1, 20, 1],
            casingVisibility,
            [isBridge],
        ),
        roadFillLayer(
            'roads_bridges_minor',
            source,
            'minor_road',
            theme.bridges_minor,
            [12, 0, 12.5, 0.5, 20, 32],
            [isBridge],
        ),

        roadCasingLayer(
            'roads_bridges_medium_casing',
            source,
            'major_road',
            theme.bridges_medium_casing,
            [7, 0, 7.5, 0.5, 20, 32],
            [10, 0, 10.5, 1, 20, 1],
            casingVisibility,
            [isBridge],
        ),
        roadFillLayer(
            'roads_bridges_medium',
            source,
            'major_road',
            theme.bridges_medium,
            [7, 0, 7.5, 0.5, 20, 32],
            [isBridge],
        ),

        roadCasingLayer(
            'roads_bridges_major_casing',
            source,
            'major_road',
            theme.bridges_major_casing,
            [7, 0, 7.5, 0.5, 19, 32],
            [9, 0, 9.5, 1, 19, 1],
            casingVisibility,
            [isBridge],
        ),
        roadFillLayer(
            'roads_bridges_major',
            source,
            'major_road',
            theme.bridges_major,
            [7, 0, 7.5, 0.5, 19, 32],
            [isBridge],
        ),

        roadCasingLayer(
            'roads_bridges_highway_casing',
            source,
            'highway',
            theme.bridges_highway_casing,
            [3, 0, 3.5, 0.5, 18, 32],
            [7, 0, 7.5, 1, 18, 1],
            casingVisibility,
            [isBridge],
        ),
        roadFillLayer(
            'roads_bridges_highway',
            source,
            'highway',
            theme.bridges_highway,
            [3, 0, 3.5, 0.5, 18, 32],
            [isBridge],
        ),

        // add missing small roads
        {
            'id': 'roads_path',
            'type': 'line',
            source,
            'source-layer': 'roads',
            'filter': [
                'all',
                ['==', ['get', 'kind'], 'path'],
                ['!', ['==', ['get', 'is_tunnel'], true]],
                ['!', ['==', ['get', 'is_bridge'], true]],
            ],
            'paint': {
                'line-color': theme.other,
                'line-dasharray': [2, 1],
                'line-width': [
                    'interpolate',
                    ['exponential', 1.6],
                    ['zoom'],
                    14,
                    0,
                    14.5,
                    0.5,
                    20,
                    12,
                ],
            },
        },
        {
            'id': 'roads_bridges_path',
            'type': 'line',
            source,
            'source-layer': 'roads',
            'filter': [
                'all',
                ['==', ['get', 'kind'], 'path'],
                ['==', ['get', 'is_bridge'], true],
            ],
            'paint': {
                'line-color': theme.bridges_other,
                'line-dasharray': [2, 1],
                'line-width': [
                    'interpolate',
                    ['exponential', 1.6],
                    ['zoom'],
                    14,
                    0,
                    14.5,
                    0.5,
                    20,
                    12,
                ],
            },
        },
        {
            'id': 'roads_tunnels_path',
            'type': 'line',
            source,
            'source-layer': 'roads',
            'filter': [
                'all',
                ['==', ['get', 'kind'], 'path'],
                ['==', ['get', 'is_tunnel'], true],
            ],
            'paint': {
                'line-color': theme.tunnel_other,
                'line-dasharray': [1, 1],
                'line-width': [
                    'interpolate',
                    ['exponential', 1.6],
                    ['zoom'],
                    14,
                    0,
                    14.5,
                    0.5,
                    20,
                    12,
                ],
            },
        },
    ];
}
